/**
 * @fileoverview Jobs List Page
 *
 * Purpose: Browse, search, sort, and filter investigation jobs.
 * URL: /dashboard/jobs
 * Protection: Requires authentication
 *
 * Features:
 * - Data table with sortable columns and faceted Type/Status filters
 * - Created / Last Activity columns with date-range filters
 * - Global search by job, entity, or practitioner
 * - New Job button → opens create dialog
 * - Click row → navigate to job detail
 * - Filters persist per user + organisation; row checkboxes drive a bulk
 *   status update through the same `updateJob` mutation as job settings
 *   (MOR-2344)
 *
 * Dependencies:
 * - Convex: jobs.queries.listJobs, jobs.queries.listJobsLastActivity,
 *   jobs.mutations.updateJob, users.queries.getCurrentUser
 * - JobsDataTable, CreateJobDialog
 * - Active org context
 */

import { useCallback, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { Briefcase, Plus } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { JobStatus } from "@cvx/jobs/schema";
import { JOB_STATUS_LABELS } from "@/lib/job-statuses";
import { useActiveOrg } from "@/utils/org-context";
import { Button } from "@/ui/primitives/button";
import { CreateJobDialog } from "./-ui.create-job-dialog";
import { JobsDataTable, type JobRow } from "./-ui.jobs-data-table";
import {
  bulkStatusFailureDescription,
  bulkStatusSuccessMessage,
  jobsFilterStorageKey,
  jobsNeedingStatusChange,
  summariseBulkStatusUpdate,
} from "./-jobs-table-filters";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/jobs/")({
  component: JobsListPage,
  beforeLoad: () => ({
    headerTitle: "Jobs",
  }),
});

// ---------------------------------------------------------------------------
// Shared card styling (matches SummarizationView group cards)
// ---------------------------------------------------------------------------

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function JobsListPage() {
  const { orgId, isLoading: orgLoading } = useActiveOrg();

  if (!orgId) {
    return orgLoading ? (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    ) : (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">
          Please select an organisation to view jobs.
        </p>
      </div>
    );
  }

  return <JobsListContent orgId={orgId} />;
}

/**
 * Inner jobs list content — only rendered when orgId is guaranteed non-null.
 *
 * Fetches all org jobs (no server-side status filter) so the client-side
 * data table can drive sorting and multi-select status/type filtering with
 * accurate facet counts.
 */
function JobsListContent({ orgId }: { orgId: Id<"organizations"> }) {
  const { data: jobs, isLoading } = useQuery(
    convexQuery(api.jobs.queries.listJobs, { orgId }),
  );

  // Derived last-activity timestamps load alongside the list rather than as
  // part of it — listJobs is already the heavy query, so the table paints
  // first and the Last Activity column fills in a moment later.
  const { data: activity } = useQuery(
    convexQuery(api.jobs.queries.listJobsLastActivity, { orgId }),
  );

  // Per-user starred jobs (MOR-932) — starred rows pin to the top of the
  // table by default while every job stays searchable.
  const { data: starredJobIds } = useQuery(
    convexQuery(api.jobs.stars.listByUser, { orgId }),
  );
  const { mutateAsync: toggleStar } = useMutation({
    mutationFn: useConvexMutation(api.jobs.stars.toggleStar),
  });
  const handleToggleStar = useCallback(
    (jobId: string) => {
      void toggleStar({ jobId: jobId as Id<"jobs"> }).catch(() => {
        toast.error("Could not update the star — please try again.");
      });
    },
    [toggleStar],
  );

  // Filters persist per user + org (MOR-2344), so the table waits for the
  // current user before restoring them.
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const filterStorageKey =
    currentUser === undefined
      ? undefined
      : jobsFilterStorageKey(currentUser?._id ?? "anonymous", orgId);

  // Bulk status update (MOR-2344) — one `updateJob` per job so each keeps
  // its own permission check, status validation, and change-log entry, and a
  // refusal on one job doesn't block the rest. The list and filter counts
  // refresh through the reactive listJobs query.
  const { mutateAsync: updateJob } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });
  const handleBulkUpdateStatus = useCallback(
    async (selected: JobRow[], status: JobStatus): Promise<string[]> => {
      const attempted = jobsNeedingStatusChange(selected, status);
      const results = await Promise.allSettled(
        attempted.map((job) =>
          updateJob({ jobId: job._id as Id<"jobs">, status }),
        ),
      );
      const summary = summariseBulkStatusUpdate(
        selected.length,
        attempted,
        results,
      );
      const statusLabel = JOB_STATUS_LABELS[status] ?? status;
      if (summary.updated > 0 || summary.failures.length === 0) {
        toast.success(bulkStatusSuccessMessage(summary, statusLabel));
      }
      if (summary.failures.length > 0) {
        const n = summary.failures.length;
        toast.error(`Couldn't update ${n} job${n === 1 ? "" : "s"}`, {
          description: bulkStatusFailureDescription(summary.failures),
        });
      }
      return summary.failures.map((failure) => failure.jobId);
    },
    [updateJob],
  );

  // Unseen agent updates per job (MOR-1013) — the Agents column. Scoped to
  // this org so the counts line up with the jobs this table actually lists.
  const { data: agentTotals } = useQuery(
    convexQuery(api.notifications.agentActivity.unseenByJob, { orgId }),
  );

  const jobsWithActivity = useMemo(() => {
    if (!jobs) return jobs;
    const byJobId = new Map(
      (activity ?? []).map((a) => [a.jobId, a.lastActivityAt]),
    );
    const starred = new Set(starredJobIds ?? []);
    const agentByJob = agentTotals?.byJob ?? {};
    return jobs.map((job) => ({
      ...job,
      lastActivityAt: byJobId.get(job._id),
      starred: starred.has(job._id),
      agentActivity: agentByJob[job._id],
    }));
  }, [jobs, activity, starredJobIds, agentTotals]);

  return (
    <div className="flex flex-1 flex-col gap-6 bg-muted p-6">
      {isLoading || filterStorageKey === undefined ? (
        <>
          <JobsPageHeader />
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-16 w-full animate-pulse rounded-lg bg-muted"
              />
            ))}
          </div>
        </>
      ) : !jobs || jobs.length === 0 ? (
        <>
          <JobsPageHeader />
          <div
            className={`${GROUP_CARD_CLASS} flex flex-col items-center justify-center gap-4 py-16`}
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
              <Briefcase className="h-8 w-8 text-muted-foreground" />
            </span>
            <div className="flex flex-col items-center gap-1">
              <h3 className="text-lg font-medium">No jobs yet</h3>
              <p className="text-sm text-muted-foreground">
                Create your first job to get started.
              </p>
            </div>
            <CreateJobDialog>
              <Button variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                Create Job
              </Button>
            </CreateJobDialog>
          </div>
        </>
      ) : (
        <JobsDataTable
          // Remount on a user/org switch so that scope's saved filters load.
          key={filterStorageKey}
          jobs={jobsWithActivity ?? jobs}
          header={<JobsPageHeader />}
          onToggleStar={handleToggleStar}
          filterStorageKey={filterStorageKey}
          onBulkUpdateStatus={handleBulkUpdateStatus}
        />
      )}
    </div>
  );
}

/**
 * Page header for the Jobs list — title + "New Job" action.
 *
 * Rendered standalone above the loading/empty states, and passed into the
 * JobsDataTable card header for the populated state so the heading lives at
 * the top of the table card.
 */
function JobsPageHeader() {
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-semibold tracking-tight">Jobs</h1>
      <CreateJobDialog>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          New Job
        </Button>
      </CreateJobDialog>
    </div>
  );
}
